import { DateString, fromDateString, toDateString, validateDateString } from "@legacy-megarax/common";
import { useProfile } from "@legacy-megarax/iam-webapp";
import { BreadcrumbMarkers, PageTitleManager } from "@legacy-megarax/ui-components";
import { isDefined } from "@legacy-megarax/utils";
import { useCustomers } from "@megarax/crm-resource-hooks";
import { useLocation, useNavigate, useParams } from "react-router-dom-v5-compat";

import { useClientManager } from "@megaron/react-clients";

import { getVisitCalendarEvents } from "./getVisitCalendarEvents";
import { getWeekDates } from "./getWeekDates";
import { getWeekNumber } from "./getWeekNumber";
import { getWorkDayCalendarEvents } from "./getWorkDayCalendarEvents";
import { useUserScheduledTradeRoutes } from "./useUserScheduledTradeRoutes";
import { useOnVisitClose, useVisitNavigation } from "./useVisitNavigation";
import { useWeekVisits } from "./useWeekVisits";
import { useWorkWeekDays } from "./useWorkWeekDays";
import { ViewLayout } from "./ViewLayout";
import { WeekCalendar } from "./WeekCalendar";
import { WeekDaySummary } from "./WeekDaySummary";
import { WeekSelect } from "./WeekSelect";
import { WorkTimeReportDialogContainer } from "./WorkTimeReportDialog";
import { getMegaraxPath } from "@megaron/megarax-uri";
import { Uuid } from "@megaron/uuid";

export const WeekViewContainer = () => {
  const params = useParams();
  const dateString = validateDateString(params.date || "").assertOk().value;
  const userId = Number(params.userId);

  const date = fromDateString(dateString);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const onGoToDay = (dateStr: DateString) =>
    navigate(
      getMegaraxPath.uri("megarax://crm/salesperson-visits/id/{id}/{view}/{date}", {
        id: String(userId),
        view: "day",
        date: dateStr,
      }),
    );
  const onChangeDateString = (dateStr: DateString) =>
    navigate(
      getMegaraxPath.uri("megarax://crm/salesperson-visits/id/{id}/{view}/{date}", {
        id: String(userId),
        view: "week",
        date: dateStr,
      }),
    );
  const goToTradeRoute = (uuid: string) =>
    navigate(getMegaraxPath.uri("megarax://crm/trade-routes/id/{id}", { id: Uuid(uuid) }));

  const weekDates = getWeekDates(date);

  const profile = useProfile({ id: userId });

  const regionQuery = useClientManager("crm")
    .regionsQuery()
    .useQuery({ ownerEmail: profile?.user?.email }, { enabled: !!profile?.user?.email });

  const { workDays, isLoading: workDaysIsLoading, refetch: reloadWorkDays } = useWorkWeekDays(weekDates, userId);

  const visitsQuery = useWeekVisits(weekDates, userId, profile?.user.email);

  const { tradeRoutes, isLoading: tradeRoutesIsLoading } = useUserScheduledTradeRoutes(weekDates, userId);

  const customerMap = useCustomers(visitsQuery.visits.map((v) => v.customerUuid));

  const { closeVisit, openVisit } = useVisitNavigation();
  const visitEvents = getVisitCalendarEvents(visitsQuery.visits, customerMap, openVisit);

  const workTimeEvents = workDays.filter(isDefined).flatMap(getWorkDayCalendarEvents);

  const openWorkTimeReportDialog = () => navigate(`${pathname}/zestawienie_czasu_pracy`);
  const isWorkTimeReportDialogOpen = pathname.endsWith("zestawienie_czasu_pracy");

  useOnVisitClose(() => {
    reloadWorkDays();
    visitsQuery.reload();
  });

  const [year, weekNumber] = getWeekNumber(date);

  if (!userId) {
    return null;
  }

  return (
    <>
      <PageTitleManager title={"Kalendarz"} />
      <BreadcrumbMarkers
        breadcrumbs={[
          { title: "Handlowcy", to: getMegaraxPath.uri("megarax://crm/regions"), id: "salespeople" },
          {
            title: profile?.displayedName ?? "?",
            to: regionQuery.data?.items[0]?.name
              ? getMegaraxPath.uri("megarax://crm/regions/id/{id}", {
                  id: encodeURIComponent(regionQuery.data?.items[0]?.name),
                })
              : getMegaraxPath.uri("megarax://crm/regions"),
            id: "salesperson",
          },
          { title: `${year}-W${weekNumber}`, id: "visitWeek" },
        ]}
      />
      {isWorkTimeReportDialogOpen && <WorkTimeReportDialogContainer onClose={() => navigate(-1)} userId={userId} />}

      <ViewLayout
        dateSelect={<WeekSelect date={date} onChange={(date) => onChangeDateString(toDateString(date))} />}
        isLoading={workDaysIsLoading || visitsQuery.isLoading || tradeRoutesIsLoading}
        calendar={
          <WeekCalendar
            events={[...visitEvents, ...workTimeEvents]}
            date={date}
            onOpenReport={openWorkTimeReportDialog}
            renderDaySummary={(date) => (
              <WeekDaySummary
                onGoToDay={onGoToDay}
                goToTradeRoute={goToTradeRoute}
                workDay={workDays?.find((w) => w?.date === date)}
                date={date}
                tradeRoute={tradeRoutes.find((route) => route?.date === date) ?? undefined}
              />
            )}
          />
        }
        closeVisit={closeVisit}
      />
    </>
  );
};
